<template>
  <!-- width is currently being set in px -->
  <pn-modal id="order-details-modal" click-outside-to-close>
    <div class="modal-wrapper">
      <div class="order-details-tab" v-if="isMobileView">
        <div class="holder">
          <pn-tablist id="nav" :value="activeTab">
            <pn-tab
              @click="activeTab = 'info'"
              :label="$t('W2P_INFO')"
              value="info"
            >
              <pn-icon
                symbol="plus-circle"
                size="medium"
                color="gray900"
              ></pn-icon>
            </pn-tab>
            <pn-tab
              @click="activeTab = 'activity'"
              :label="$t('W2P_ACTIVITY')"
              value="activity"
            >
              <pn-icon
                symbol="envelope"
                size="medium"
                color="gray900"
              ></pn-icon>
            </pn-tab>
            <pn-icon
              @click="close()"
              symbol="close"
              color="gray900"
              size="medium"
              solid
            ></pn-icon>
          </pn-tablist>
        </div>
      </div>

      <div
        class="activity-section"
        v-if="!isMobileView || (isMobileView && activeTab === 'activity')"
      >
        <div class="header">
          <h2>{{ $t('W2P_ACTIVITY') }}</h2>
        </div>
        <div class="content">
          <order-activity-component
            v-if="!error && !order.product.ownMaterial"
            :order="order"
            :loading="loading"
          ></order-activity-component>

          <div v-else>
            <p class="bold">{{ $t('W2P_NOT_AVAILABLE_MESSAGE') }}</p>
          </div>
        </div>
      </div>
      <div
        class="info-section"
        v-if="!isMobileView || (isMobileView && activeTab === 'info')"
      >
        <div class="scroll-shade top" v-show="showShadeTop"></div>
        <div class="header">
          <h2>{{ $t(titleTranslationString) }} # {{ cartId }}</h2>
          <div class="download-icon-container">
            <pn-icon
              @click="downloadReceipt()"
              v-if="order"
              symbol="download"
              color="blue700"
              size="medium"
              solid
            ></pn-icon>
            <pn-icon
              @click="close()"
              symbol="close"
              color="gray900"
              size="medium"
              solid
            ></pn-icon>
          </div>
        </div>
        <div class="content" @scroll="onScroll">
          <order-info-component
            v-if="!error"
            :order="order"
            :isMobileView="isMobileView"
            :loading="loading"
            @downloadReceipt="downloadReceipt()"
            @orderCancelled="orderCancelled"
          >
          </order-info-component>
          <pn-toast
            v-if="error"
            icon="alert-exclamation-circle"
            appearance="warning"
          >
            <p>
              {{ $t('W2P_GET_ORDER_DETAILS_ERROR_MESSAGE') }}
            </p>
          </pn-toast>
        </div>
      </div>
    </div>
  </pn-modal>
</template>

<script>
import moment from 'moment'
import axios from 'axios'
import { mapState } from 'vuex'
import * as helper from '@/helpers/utilHelper'
import * as orderHelper from '@/helpers/orderHelper.js'

import OrderActivityComponent from '@/components/order-details-modal/OrderActivityComponent.vue'
import OrderInfoComponent from '@/components/order-details-modal/OrderInfoComponent.vue'

export default {
  name: 'OrderDetailsModalComponent',
  props: {
    orderData: {
      type: Object
    }
  },
  components: {
    OrderActivityComponent,
    OrderInfoComponent
  },
  data() {
    return {
      order: null,
      isMobileView: this.checkViewWidth(window.innerWidth),
      activeTab: 'info',
      showShadeTop: false,
      showShadeBottom: true,
      error: false,
      loading: false
    }
  },
  watch: {
    orderData({ order }) {
      if (order) {
        this.getOrderDetails()
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
  },
  computed: {
    ...mapState(['accessToken']),
    cartId() {
      return this.orderData.order ? this.orderData.order.cartId : ''
    },
    titleTranslationString() {
      if (!this.orderData.order) {
        return ''
      }

      return helper.getCatogeryTranslationString(
        this.orderData.order?.product?.siteCategory
      )
    }
  },
  methods: {
    onScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      this.showShadeTop = scrollTop !== 0
      this.showShadeBottom = scrollTop + clientHeight < scrollHeight
    },
    close() {
      setTimeout(() => {
        this.order = null
        this.error = false
        document.getElementById('order-details-modal').close()
        this.$emit('order-detail-modal-closed')
      }, 100)
    },
    formatTime(timestamp) {
      return {
        date: moment(timestamp).format('D MMM'),
        time: moment(timestamp).format('HH:mm')
      }
    },
    downloadReceipt() {
      if (this.receiptAvailable()) {
        window.open(this.order.receiptUrl)
      } else if (this.odrConfirmationAvailable) {
        window.open(this.order.odrUrls['odrConfirmation.pdf'])
      }
    },
    receiptAvailable() {
      return !!this.order.receiptUrl
    },
    odrConfirmationAvailable() {
      return !!this.order.odrUrls['odrConfirmation.pdf']
    },
    onWindowResize(e) {
      const width = e.target.innerWidth

      this.isMobileView = this.checkViewWidth(width)
    },
    checkViewWidth(width) {
      return width <= 768
    },
    getOrderDetails() {
      this.loading = true

      axios({
        method: 'get',
        url:
          process.env.VUE_APP_W2P_API_BASE_URL +
          `/orders/orders/${this.orderData.order.orderId}`,
        headers: { authorization: this.accessToken }
      })
        .then(response => {
          this.loading = false
          this.order = orderHelper.mapOrder(response.data)
        })
        .catch(() => (this.error = true))
    },
    orderCancelled() {
      this.$emit('refreshList')
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-wrapper {
  display: flex;
  flex-direction: row;
  height: 100%;
  overflow: hidden;

  .order-details-tab {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 3;
    .holder {
      width: 100%;
      padding-top: 1em;
      background-color: white;
      #nav {
        .pn-tablist {
          pn-tab:first-child {
            margin-left: 1.5em;
          }

          pn-tab {
            margin-right: 1.5em;
          }

          & > pn-icon {
            margin: auto 2em auto auto;

            &:hover {
              cursor: pointer;
            }
          }
        }
      }
    }
  }

  .activity-section {
    width: 35%;
    display: flex;
    flex-direction: column;
    border-right: 0.063em solid $gray200;

    .header {
      padding: 2em 2.5em;
      border-bottom: 0.125em solid $gray50;
    }

    .content {
      padding: 2.5em;
      flex: 1;

      overflow-y: scroll;

      /* Scrollbar width */
      &::-webkit-scrollbar {
        background-color: $white;
        width: 0.75em;
      }

      /* Scrollbar button */
      &::-webkit-scrollbar-button {
        display: none;
      }

      /* Track */
      &::-webkit-scrollbar-track {
        background-color: $white;
      }

      /* Handle */
      &::-webkit-scrollbar-thumb {
        background-color: $gray400;
        border-radius: 1em;
        border: 4px solid $white;
      }

      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: $gray700;
      }
    }
  }

  .info-section {
    width: 65%;
    display: flex;
    flex-direction: column;
    padding: 0 3em;

    .header {
      padding: 2em 0;
      display: flex;
      justify-content: space-between;

      h2 {
        margin-top: auto;
      }

      .download-icon-container {
        display: flex;
      }

      & > div {
        pn-icon:hover {
          cursor: pointer;
        }

        pn-icon:last-child {
          margin-left: 2em;
        }
      }
    }

    .content {
      flex: 1;
      overflow-y: scroll;

      /* Scrollbar width */
      &::-webkit-scrollbar {
        background-color: $white;
        width: 0.75em;
      }

      /* Scrollbar button */
      &::-webkit-scrollbar-button {
        display: none;
      }

      /* Track */
      &::-webkit-scrollbar-track {
        background-color: $white;
      }

      /* Handle */
      &::-webkit-scrollbar-thumb {
        background-color: $gray400;
        border-radius: 1em;
        border: 4px solid $white;
      }

      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: $gray700;
      }
    }

    .scroll-shade {
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        $white 87.46%,
        $white 100%
      );
      height: 72px;
      width: 65%;
      position: absolute;
      z-index: 2;

      &.top {
        top: 5.3em;
        right: 0;
        transform: scaleY(-1);
      }
      &.bottom {
        bottom: 0;
        right: 0;
      }
    }
  }
}

// 768px (48em) - using px here as it is also used in the vm
@media screen and (max-width: 768px) {
  .modal-wrapper {
    flex-direction: column;

    .activity-section,
    .info-section {
      width: 100%;
      border: none;
      height: -webkit-fill-available;

      .header {
        display: none;
        border-bottom: none;
      }

      .scroll-shade {
        display: none;
      }
    }

    .info-section {
      padding: 2em 1.5em;
    }
  }
}
</style>
